import React, { useCallback, useEffect, useState, VFC, KeyboardEventHandler, useRef } from 'react';
import { TextInput } from 'src/common/form/text-input';

import { ToggleComponent } from 'src/common/form/toggle/component';
import { useSharePanelStore } from 'src/common/store/share.store';
import { AddStrongIcon, ChevronRightRegularIcon } from 'src/monet/icons';
import { ShareLinkFieldType } from '../link-config-form.factory';
import { NDA_TEMPLATE_URL } from 'src/utils/copy';
import { useCurrentSubscription } from 'src/store/subscription';
import FileDropzone from 'src/common/FileDropzone';
import { uploadFileToS3 } from 'src/common/helpers/file-upload';
import { DocumentIcon } from '@heroicons/react/outline';
import { Button } from 'src/common/button';
import LoadingSpinner from 'src/common/components/loading-spinner';
import OrganizationLink from 'src/common/OrganizationLink';
import { useFeatureRestrictions } from 'src/hooks/useFeatureRestrictions';

interface ShareNdaLegalEntityProps {
  ndaLegalEntityName: ShareLinkFieldType<string>;
  customNdaTemplateUrl: ShareLinkFieldType<string>;
  onFieldChange: <T>(payload: ShareLinkFieldType<T>) => void;
}

interface ShareNdaProps extends ShareNdaLegalEntityProps {
  ndaRequired: ShareLinkFieldType<boolean>;
  emailRequired: ShareLinkFieldType<boolean>;
  emailVerificationRequired: ShareLinkFieldType<boolean>;
}

const ShareNdaLegalEntity = ({ ndaLegalEntityName, customNdaTemplateUrl, onFieldChange }: ShareNdaLegalEntityProps) => {
  const onLegalEntityNameChange: KeyboardEventHandler = (event) => {
    const { value } = event.target as HTMLInputElement;
    onFieldChange({ field: ndaLegalEntityName.field, value });
  };
  const [isUploading, setIsUploading] = useState(false);
  const fileUploadRef = useRef<HTMLElement | null>(null);

  const pushLinkConfigType = useSharePanelStore((state: any) => state.pushLinkConfigType);

  const onClickSignatures = () => {
    pushLinkConfigType('signatures');
  };

  const onCustomNdaUpload = useCallback(
    async (files: File[]) => {
      if (files.length > 0) {
        const file = files[0];
        try {
          // Upload the file to S3
          setIsUploading(true);
          const uploadedFile = await uploadFileToS3(file, 'nda-signatures');
          // Update the customNdaTemplateUrl field with the S3 URL
          onFieldChange({ field: customNdaTemplateUrl.field, value: uploadedFile.url });
        } catch (error) {
          console.error('Error uploading custom NDA template:', error);
        } finally {
          setIsUploading(false);
        }
      }
    },
    [onFieldChange, customNdaTemplateUrl]
  );
  const onUploadClicked = () => {
    if (fileUploadRef.current) {
      fileUploadRef.current.click();
    }
  };
  const renderFileUploader = useCallback(() => {
    return (
      <FileDropzone
        accept='.pdf'
        type='file'
        className='file-dropzone'
        multiple={false}
        onFilesUpdate={onCustomNdaUpload}
        resetOnSuccess={true}
        dropzoneClasses={null}
        inputRef={fileUploadRef}
      />
    );
  }, [onCustomNdaUpload]);

  const renderUploadContent = useCallback(
    () => (
      <div className='w-full h-full flex flex-col items-center justify-center'>
        {isUploading ? (
          <LoadingSpinner />
        ) : customNdaTemplateUrl.value ? (
          <>
            <div className='flex items-center mb-2'>
              <DocumentIcon className='w-4 h-4 mr-1' />
              <a
                href={customNdaTemplateUrl.value}
                target='_blank'
                rel='noopener noreferrer'
                className='text-bedrock-brand-primary hover:underline text-sm'
              >
                View uploaded NDA template
              </a>
            </div>
            <button onClick={onUploadClicked} className='text-bedrock-brand-primary underline cursor-pointer text-sm'>
              Replace
            </button>
          </>
        ) : (
          <Button size='regular' variant='tertiary' icon={<AddStrongIcon />} label='Upload' onClick={onUploadClicked} />
        )}
      </div>
    ),
    [customNdaTemplateUrl.value, isUploading]
  );

  const { isOnTrial, isEnterprise } = useCurrentSubscription((state) => state);

  return (
    <div className='flex flex-col text-bedrock-black'>
      <span className='text-bedrock-p-strong text-bedrock-black'>Company legal name</span>
      <span className='text-bedrock-p-small mt-1'>
        The full legal name of the entity that the viewer will be signing the NDA with
      </span>
      <div className='mt-4'>
        <TextInput
          id='nda-legal-entity-name'
          inputProps={{
            value: ndaLegalEntityName.value,
            placeholder: 'e.g. Box Inc.',
          }}
          onChange={onLegalEntityNameChange}
        />
      </div>
      <div className='mt-4'>
        <span className='text-bedrock-p-strong text-bedrock-black'>Custom NDA Template (Optional)</span>
        {(isEnterprise || isOnTrial) ? (
          <>
            <div className='hidden'>{renderFileUploader()}</div>
            <div className='flex flex-col mt-2'>
              <div className='flex relative w-full h-[112px] bg-bedrock-light-gray rounded-lg items-center justify-center overflow-hidden'>
                {renderUploadContent()}
              </div>
            </div>
          </>
        ) : (
          <div className='mt-2'>
            <OrganizationLink className='btn-bedrock-primary' to='/billing'>
             Upgrade to Scale
            </OrganizationLink>
          </div>
        )}
      </div>
      <div className='mt-6'>
        <div className='flex items-center justify-between has-hover cursor-pointer' onClick={onClickSignatures}>
          <div className='text-bedrock-p-strong text-bedrock-black'>View signatures</div>
          <ChevronRightRegularIcon />
        </div>
      </div>
    </div>
  );
};

export const ShareNda: VFC<ShareNdaProps> = ({
  emailRequired,
  emailVerificationRequired,
  ndaRequired,
  ndaLegalEntityName,
  customNdaTemplateUrl,
  onFieldChange,
}) => {
  const [ndaRequiredEnabled, setNdaRequiredEnabled] = useState(ndaRequired.value);
  const { features, showUpgradeModal } = useFeatureRestrictions();

  const onEmailRequiredToggleChanged = (value: boolean) => {
    // Check if user can require NDA
    if (!features.canRequireNDA) {
      return;
    }

    setNdaRequiredEnabled(value);
    onFieldChange({
      field: ndaRequired.field,
      value: value,
    });
    // If NDA value is enabled, then we need to make sure that email is required and email verification is enabled
    onFieldChange({
      field: emailRequired.field,
      value: value,
    });
    onFieldChange({
      field: emailVerificationRequired.field,
      value: value,
    });
  };

  return (
    <div className='flex flex-col space-y-6 pt-2 pb-6'>
      <div className='flex flex-col space-y-sm'>
        <ToggleComponent
          checked={ndaRequiredEnabled}
          onChange={onEmailRequiredToggleChanged}
          label='Require an NDA'
          labelType='strong'
          disabled={!features.canRequireNDA}
        />
        <div className='text-bedrock-p-small text-bedrock-black'>
          Require the viewer to electronically sign a{' '}
          <a href={NDA_TEMPLATE_URL} className='underline' target='_blank' rel='noopener noreferrer'>
            non-disclosure agreement
          </a>{' '}
          before viewing this Journey.
        </div>
      </div>
      {ndaRequiredEnabled && (
        <>
          <ShareNdaLegalEntity
            ndaLegalEntityName={ndaLegalEntityName}
            customNdaTemplateUrl={customNdaTemplateUrl}
            onFieldChange={onFieldChange}
          />
        </>
      )}
    </div>
  );
};
